import React from 'react';
import theme from '@/style';
import {COLORS} from '../constant';

export type BetsItemTarget = HTMLElement | null;

export interface BetsItemProps {
  type: 'A' | 'B' | 'C' | 'D';
  onItemPress?: (v?: BetsItemTarget) => void;
  onDisabledPress?: () => void;
  isDigit?: boolean;
  digit?: string;
  rate?: string;
  betsType?: string;
  disabled?: boolean;
  showDisabledOpacity?: boolean;
  checked?: boolean;
  isPrimary?: boolean;
}

const BetsItem = ({
  isDigit = false,
  digit,
  rate,
  disabled,
  showDisabledOpacity = true,
  type = 'A',
  isPrimary = false,
  checked = false,
  onDisabledPress,
  onItemPress,
}: BetsItemProps) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const borderColor = React.useMemo(() => {
    if (isPrimary) {
      return checked ? theme.basicColor.primary : '#9FA5AC';
    }
    return COLORS[type];
  }, [checked, isPrimary, type]);

  const backgroundColor = React.useMemo(() => {
    if (checked) {
      return isPrimary ? theme.basicColor.primary : COLORS[type];
    }
    return 'var(--basic-color-white)';
  }, [checked, isPrimary, type]);

  const wrapperStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      ...(isDigit
        ? {
            width: '20%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '-0.5rem',
          }
        : {
            flex: 1,
            padding: '4px',
            margin: '-4px',
            boxSizing: 'border-box',
          }),
      opacity: disabled && showDisabledOpacity ? 0.3 : 1,
      position: 'relative',
      overflow: 'visible',
    };
  }, [disabled, isDigit, showDisabledOpacity]);

  const labelStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      width: isDigit ? 'auto' : '100%',
      pointerEvents: disabled ? 'none' : 'auto',
      opacity: 1,
      cursor: disabled ? 'default' : 'pointer',
      userSelect: 'none',
      touchAction: 'manipulation',
    };
  }, [disabled, isDigit]);

  const innerStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      justifyContent: isDigit ? 'center' : undefined,
      width: isDigit ? '2.5rem' : '100%',
      height: isDigit ? '2.5rem' : undefined,
      minWidth: isDigit ? '2.5rem' : undefined,
      minHeight: isDigit ? '2.5rem' : undefined,
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor,
      backgroundColor,
      boxShadow: checked
        ? '0 0 0 0 #0000, 0 0 0 0 #0000'
        : 'rgba(0, 0, 0, 0.25) 4px 4px 8px 0px, rgb(255, 255, 255) -4px -4px 8px 0px',
      boxSizing: 'border-box',
      transitionDuration: '0s',
      overflow: 'visible',
      flexShrink: 0,
    };
  }, [backgroundColor, borderColor, checked, isDigit]);

  const inputStyle = React.useMemo<React.CSSProperties>(() => {
    return {
      display: 'none',
    };
  }, []);

  return (
    <div style={wrapperStyle}>
      <div
        className={isDigit ? 'flex items-center justify-center' : ''}
        ref={containerRef}
        onClick={() => {
          if (disabled) {
            onDisabledPress?.();
            return;
          }
          onItemPress?.(containerRef.current);
        }}>
        <label
          aria-label={digit}
          className="duration-200 block"
          style={labelStyle}
          onClick={e => {
            e.preventDefault();
          }}>
          <input
            type="checkbox"
            value={digit}
            disabled={disabled}
            style={inputStyle}
            readOnly
          />
          <div
            className={
              'relative duration-200 flex flex-col items-center border-2 ' +
              (isDigit
                ? 'rounded-full size-10 justify-center'
                : 'rounded-lg p-3')
            }
            style={innerStyle}>
            <span
              className={`font-bold duration-200 leading-4 text-sm text-center ${
                checked ? 'text-white' : 'text-main'
              }`}>
              {digit}
            </span>
            {!isDigit && rate ? (
              <span
                className={`text-xs duration-200 leading-3 mt-1 text-center ${
                  checked ? 'text-white' : 'text-acc'
                }`}>
                {rate}
              </span>
            ) : null}
          </div>
        </label>
      </div>
    </div>
  );
};

export default React.memo(BetsItem);
